<script setup>
import { Drawer } from '@/components/ui/drawer';
import { Collapsible } from '@/components/ui/collapsible';
import { IconChevronRight, IconLogout, IconSettings } from '@tabler/icons-vue';
import { computed } from 'vue';
import { useUser } from '@/composables/useUser';
import { Link } from '@inertiajs/vue3';
import { useSidebarStore } from '@/stores/sidebarStore.js';
import SidebarItem from '@/components/SidebarItem.vue';
import SidebarGroup from '@/components/SidebarGroup.vue';
import { useAppNavigation } from '@/composables/useAppNavigation';

const store = useSidebarStore();

const sidebarOpen = computed({
    get() {
        return store.open;
    },
    set(newValue) {
        store.toggleSidebar(newValue);
    },
});

const user = useUser();
const { navigation, adminNavigation, settingsLink } = useAppNavigation();
</script>

<template>
    <Drawer
        v-model:open="sidebarOpen"
        direction="left"
        size="sm"
        :handle="false"
        :closable="false"
        content-class="lg:hidden"
    >
        <div class="-mx-5 -my-4 flex grow flex-col bg-white">
            <div class="flex shrink-0 items-center justify-center px-6 py-2">
                <img
                    class="h-16 w-auto"
                    src="/images/logo-kleur.png"
                    alt="Maat Stalling"
                />
            </div>

            <nav class="flex-1 overflow-y-auto overflow-x-hidden pb-6">
                <ul
                    role="list"
                    class="flex flex-1 flex-col gap-y-7 px-6"
                >
                    <li>
                        <ul
                            role="list"
                            class="-mx-2 space-y-1"
                        >
                            <li
                                v-for="item in navigation"
                                :key="item.name"
                            >
                                <Link
                                    v-if="!item.children"
                                    :href="item.href"
                                    :class="[
                                        item.current
                                            ? 'font-semibold text-primary-600'
                                            : 'text-gray-900 hover:bg-gray-50',
                                        'group flex min-h-11 items-center gap-x-3 rounded-md p-2 text-sm leading-6',
                                    ]"
                                    @click="sidebarOpen = false"
                                >
                                    <component
                                        :is="item.icon"
                                        :class="[
                                            item.current
                                                ? 'text-primary-600'
                                                : 'text-primary-400 group-hover:text-primary-600',
                                            'size-5 shrink-0',
                                        ]"
                                        aria-hidden="true"
                                    />
                                    {{ item.name }}
                                </Link>

                                <Collapsible
                                    v-else
                                    :default-open="item.current"
                                >
                                    <template #trigger="{ open }">
                                        <button
                                            type="button"
                                            :class="[item.current ? 'bg-gray-50' : 'hover:bg-gray-50', 'flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm/6 font-semibold text-gray-700']"
                                        >
                                            <component
                                                :is="item.icon"
                                                class="size-6 shrink-0 text-gray-400"
                                                aria-hidden="true"
                                            />
                                            {{ item.name }}
                                            <IconChevronRight
                                                :class="[open ? 'rotate-90 text-gray-500' : 'text-gray-400', 'ml-auto size-5 shrink-0']"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </template>

                                    <ul class="mt-1 px-2">
                                        <li
                                            v-for="subItem in item.children"
                                            :key="'subitem' + subItem.name"
                                        >
                                            <Link
                                                :href="subItem.href"
                                                :class="[subItem.current ? 'bg-gray-50' : 'hover:bg-gray-50', 'block rounded-md py-2 pr-2 pl-9 text-sm/6 text-gray-700']"
                                                @click="sidebarOpen = false"
                                            >
                                                {{ subItem.name }}
                                            </Link>
                                        </li>
                                    </ul>
                                </Collapsible>
                            </li>
                        </ul>
                    </li>

                    <li v-if="user.is_admin">
                        <ul
                            role="list"
                            class="-mx-2 space-y-1"
                        >
                            <li
                                class="text-xs font-semibold uppercase tracking-wider text-gray-400"
                            >
                                Admin
                            </li>
                            <li
                                v-for="item in adminNavigation"
                                :key="item.name"
                            >
                                <Link
                                    v-if="!item.children"
                                    :href="item.href"
                                    :class="[
                                        item.current
                                            ? 'font-semibold text-primary-600'
                                            : 'text-gray-900 hover:bg-gray-50 hover:text-black',
                                        'group flex min-h-11 items-center gap-x-4 rounded-md p-2 text-sm leading-6',
                                    ]"
                                    @click="sidebarOpen = false"
                                >
                                    <component
                                        :is="item.icon"
                                        :class="[
                                            item.current
                                                ? 'text-primary-500'
                                                : 'text-primary-400 group-hover:text-gray-700',
                                            'size-5 shrink-0',
                                        ]"
                                        aria-hidden="true"
                                    />
                                    {{ item.name }}
                                </Link>

                                <Collapsible
                                    v-else
                                    :default-open="item.current"
                                >
                                    <template #trigger="{ open }">
                                        <button
                                            type="button"
                                            :class="[
                                                item.current
                                                    ? 'font-semibold text-primary-600'
                                                    : 'text-gray-900 hover:bg-gray-50 hover:text-black',
                                                'group flex min-h-11 w-full items-center gap-x-4 rounded-md p-2 text-left text-sm leading-6',
                                            ]"
                                        >
                                            <component
                                                :is="item.icon"
                                                :class="[
                                                    item.current
                                                        ? 'text-primary-500'
                                                        : 'text-primary-400 group-hover:text-gray-700',
                                                    'size-5 shrink-0',
                                                ]"
                                                aria-hidden="true"
                                            />
                                            {{ item.name }}
                                            <IconChevronRight
                                                :class="[open ? 'rotate-90 text-gray-500' : 'text-gray-400', 'ml-auto size-5 shrink-0']"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </template>

                                    <ul class="mt-1 px-2">
                                        <li
                                            v-for="subItem in item.children"
                                            :key="'subitem' + subItem.name"
                                        >
                                            <Link
                                                :href="subItem.href"
                                                :class="[
                                                    subItem.current
                                                        ? 'font-semibold text-primary-600'
                                                        : 'text-gray-900 hover:bg-gray-50 hover:text-black',
                                                    'block rounded-md py-2 pr-2 pl-9 text-sm leading-6',
                                                ]"
                                                @click="sidebarOpen = false"
                                            >
                                                {{ subItem.name }}
                                            </Link>
                                        </li>
                                    </ul>
                                </Collapsible>
                            </li>
                        </ul>
                    </li>
                </ul>
            </nav>

            <div
                class="mt-auto flex shrink-0 flex-col border-t border-gray-200 px-6 py-2"
            >
                <Link
                    :href="settingsLink.href"
                    :class="[
                        settingsLink.current
                            ? 'font-semibold text-primary-600'
                            : 'text-gray-900 hover:bg-gray-50 hover:text-black',
                        'group -mx-2 flex min-h-11 items-center gap-x-4 rounded-md p-2 text-sm leading-6',
                    ]"
                    @click="sidebarOpen = false"
                >
                    <IconSettings
                        :class="[
                            settingsLink.current
                                ? 'text-primary-500'
                                : 'text-primary-400 group-hover:text-primary-600',
                            'size-5 shrink-0',
                        ]"
                        aria-hidden="true"
                    />
                    Instellingen
                </Link>
                <Link
                    :href="route('logout')"
                    method="post"
                    as="button"
                    type="submit"
                    class="group -mx-2 flex min-h-11 w-full items-center gap-x-4 rounded-md p-2 text-sm leading-6 text-gray-900 hover:bg-gray-50 hover:text-black"
                >
                    <IconLogout
                        class="size-5 shrink-0 text-primary-400 group-hover:text-primary-600"
                        aria-hidden="true"
                    />
                    Uitloggen
                </Link>
            </div>
        </div>
    </Drawer>

    <div class="hidden pt-16 lg:fixed lg:inset-y-0 lg:z-sidebar lg:flex lg:w-56 lg:flex-col">
        <div
            class="layout-sidebar bg-gray-50 flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-300"
        >
            <nav class="mt-6 flex flex-1 flex-col pb-4">
                <ul
                    role="list"
                    class="flex flex-1 flex-col gap-y-7"
                >
                    <SidebarGroup>
                        <SidebarItem
                            v-for="item in navigation"
                            :key="item.name"
                            :item="item"
                        />
                    </SidebarGroup>

                    <SidebarGroup v-if="user.is_admin">
                        <li class="px-6 text-sm font-medium leading-6 text-gray-700">
                            Admin
                        </li>

                        <SidebarItem
                            v-for="item in adminNavigation"
                            :key="item.name"
                            :item="item"
                        />
                    </SidebarGroup>

                    <SidebarGroup class="mt-auto">
                        <SidebarItem :item="settingsLink" />
                    </SidebarGroup>
                </ul>
            </nav>
        </div>
    </div>
</template>
